import { formatDate, formatHectares, wetlandTypeLabel } from "./format.ts";
import {
  changesetUrl,
  elementUrl,
  historyUrl,
  idEditorUrl,
  josmUrl,
  keyWikiUrl,
  osmchaUrl,
  type Ref,
  userUrl,
  valueUrl,
} from "./osm.ts";
import type { Detail, Store } from "./store.ts";

function Out({ href, children }: { href: string; children: unknown }) {
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

function title(detail: Detail): string {
  const tags = detail.element?.tags ?? {};
  return tags.name ?? wetlandTypeLabel(tags.wetland) ?? "Wetland";
}

function Area({ area }: { area: number | null }) {
  if (!area) return null;
  return (
    <p class="area">
      <strong>{formatHectares(area)}</strong>
    </p>
  );
}

function TagTable({ tags }: { tags: Record<string, string> }) {
  const keys = Object.keys(tags).sort();
  if (keys.length === 0) return <p class="secondary">No tags.</p>;

  return (
    <table class="tags">
      <thead>
        <tr>
          <th>Key</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((key) => {
          const value = tags[key] as string;
          const href = valueUrl(key, value);
          return (
            <tr>
              <td>
                <Out href={keyWikiUrl(key)}>{key}</Out>
              </td>
              <td>{href ? <Out href={href}>{value}</Out> : value}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function MetaTable({ detail }: { detail: Detail }) {
  const element = detail.element;
  if (!element) return null;
  const ref: Ref = { type: element.type, id: element.id };

  return (
    <table class="meta">
      <tbody>
        <tr>
          <th>Element</th>
          <td>
            <Out href={elementUrl(ref)}>
              {element.type}/{element.id}
            </Out>
          </td>
        </tr>
        <tr>
          <th>Version</th>
          <td>
            <Out href={historyUrl(ref)}>v{element.version}</Out>
          </td>
        </tr>
        <tr>
          <th>Last edited</th>
          <td title={element.timestamp}>{formatDate(element.timestamp)}</td>
        </tr>
        <tr>
          <th>By</th>
          <td>
            {element.user ? <Out href={userUrl(element.user)}>{element.user}</Out> : "unknown"}
          </td>
        </tr>
        <tr>
          <th>Changeset</th>
          <td>
            <Out href={changesetUrl(element.changeset)}>{element.changeset}</Out>{" "}
            <span class="secondary">
              (<Out href={osmchaUrl(element.changeset)}>OSMCha</Out>)
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function Links({ ref }: { ref: Ref }) {
  return (
    <p class="link-list">
      <Out href={elementUrl(ref)}>View on osm.org</Out>
      <Out href={idEditorUrl(ref)}>Edit in iD</Out>
      <Out href={josmUrl(ref)}>Edit in JOSM</Out>
      <Out href={historyUrl(ref)}>History</Out>
    </p>
  );
}

export function Sidebar({ store }: { store: Store }) {
  const detail = store.detail;
  if (!detail) return null;

  const tags = detail.element?.tags ?? {};

  return (
    <aside class="sidebar">
      <div class="sidebar-header">
        <h2>{detail.loading ? "Loading…" : title(detail)}</h2>
        <button
          type="button"
          class="close"
          title="Close"
          aria-label="Close"
          onclick={() => store.deselect()}
        >
          ×
        </button>
      </div>

      {detail.error ? (
        <div class="sidebar-body">
          <p class="error">
            Could not load {detail.ref.type}/{detail.ref.id} from the OpenStreetMap API.
          </p>
          <p class="secondary">{detail.error.message}</p>
          <p>
            <button type="button" onclick={() => store.select(detail.ref)}>
              Try again
            </button>
          </p>
        </div>
      ) : detail.loading ? (
        <div class="sidebar-body">
          <p class="secondary">Loading…</p>
        </div>
      ) : (
        <div class="sidebar-body">
          <Area area={detail.area} />

          <h3>Tags</h3>
          <TagTable tags={tags} />

          <h3>Metadata</h3>
          <MetaTable detail={detail} />

          <h3>Open in</h3>
          <Links ref={detail.ref} />
        </div>
      )}
    </aside>
  );
}
